import React, { useState } from 'react';
import { useWebSocket } from '../../connections/WebSocketContext.js';
import './LoadMeeting.css';

const LoadMeeting = ({ onMeetingLoaded, onStartMeeting }) => {
  const [meetingId, setMeetingId] = useState('');
  const [meetingData, setMeetingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);

  // Get WebSocket functions and state
  const { isConnected, connect, disconnect, sendMessage, subscribeToInstance, meetingData: wsMeetingData, serverError, clearServerError } = useWebSocket();

  // Handle meeting data received from WebSocket
  React.useEffect(() => {
    if (wsMeetingData && !meetingData) {
      console.log('📋 Meeting data received from WebSocket:', wsMeetingData);
      setMeetingData(wsMeetingData);
      setLoading(false); // Stop loading when data is received
    }
  }, [wsMeetingData, meetingData]);

  // Handle server errors
  React.useEffect(() => {
    if (serverError) {
      setError(serverError);
      setLoading(false);
    }
  }, [serverError]);

  // Auto-connect to WebSocket when component mounts
  React.useEffect(() => {
    if (!isConnected) {
      connect().catch(err => {
        setError('Failed to connect to WebSocket: ' + err.message);
      });
    }
  }, [isConnected, connect]);

  // Add timeout for loading state
  React.useEffect(() => {
    if (loading) {
      const timeout = setTimeout(() => {
        setLoading(false);
        setError('Timeout: No response from server. Please try again.');
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [loading]);



  const handleLoadMeeting = async () => {
    if (!meetingId.trim()) {
      setError('Please enter a meeting ID');
      return;
    }

    if (!isConnected) {
      setError('Please connect to WebSocket first');
      return;
    }

    setLoading(true);
    setError(null);
    clearServerError(); // Clear any previous server errors

    try {
      // Subscribe to meeting updates using the new pub/sub system
      subscribeToInstance(meetingId.trim(), 'viewer');
      console.log('✅ Subscribed to meeting updates as viewer:', meetingId.trim());
      
      // Note: Meeting details are now fetched via API call in the parent Meeting component
      // No need to send meeting-details message via WebSocket
      
      // The meeting data will be received via WebSocket and handled by the useEffect above
      // Loading state will be cleared when data is received or timeout occurs
    } catch (err) {
      setError(err.message || 'Failed to load meeting');
      setLoading(false);
    }
  };

  const handleStartMeeting = () => {
    if (meetingData && onStartMeeting) {
      // First notify parent about the loaded meeting data
      onMeetingLoaded(meetingData);
      // Then start the meeting
      onStartMeeting(meetingData);
    }
  };

  const truncateDescription = (description) => {
    if (!description) return '';
    const lines = description.split('\n');
    if (lines.length <= 2) return description;
    return lines.slice(0, 2).join('\n');
  };

  const renderStartScreen = () => (
    <div className="start-screen">
      <div className="meeting-input-section">
        <div className="input-group">
          <input
            type="text"
            value={meetingId}
            onChange={(e) => setMeetingId(e.target.value)}
            placeholder="Enter meeting number"
            className="meeting-input"
            onKeyPress={(e) => e.charCode === 13 && handleLoadMeeting()}
            disabled={!isConnected}
          />
          <button 
            onClick={handleLoadMeeting}
            disabled={loading || !isConnected}
            className="load-meeting-btn"
          >
            {loading ? 'Loading...' : (!isConnected ? 'Connecting...' : 'Load Meeting')}
          </button>
        </div>
        {!isConnected && (
          <p className="connection-hint">
            ⚠️ Connecting to server... Please wait
          </p>
        )}
      </div>
    </div>
  );

  const renderMeetingInfo = () => {
    // Handle the nested data structure from WebSocket response
    const entityData = meetingData.data?.entity || meetingData.entity;
    const meetingName = entityData?.name || 'Meeting';
    const meetingDescription = entityData?.description || '';
    
    return (
      <div className="meeting-info">
        <h2>Meeting Details</h2>
        
        <div className="meeting-details">
          <div className="meeting-name">
            <h3>{meetingName}</h3>
          </div>
          
          <div className="meeting-description">
            <h4>Description:</h4>
            <div className="description-content">
              {descriptionExpanded ? (
                <p>{meetingDescription}</p>
              ) : (
                <p>{truncateDescription(meetingDescription)}</p>
              )}
              {meetingDescription && meetingDescription.split('\n').length > 2 && (
                <button 
                  onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                  className="expand-btn"
                >
                  {descriptionExpanded ? 'Show Less' : 'Show More'}
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="start-meeting-section">
          <button 
            onClick={handleStartMeeting}
            className="start-meeting-btn"
          >
            Start Meeting
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="load-meeting-container">
      {error && (
        <div className="error-message">
          <div className="error-icon">⚠️</div>
          <div className="error-content">
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={() => setError(null)} className="dismiss-btn">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {!meetingData ? renderStartScreen() : renderMeetingInfo()}
    </div>
  );
};

export default LoadMeeting; 
